import React, { useState, useEffect } from 'react';
import { CalendarProvider } from '../../contexts/CalendarContext';
import Calendar from '../Calendar/Calendar';
import EventModal from '../Calendar/EventModal';
import { bookingService } from '../../api/apiService';

const CalendarPage = () => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    event: null,
    mode: 'view' // 'view', 'edit', 'create'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Event click handler
  const handleEventClick = (event) => {
    setModalState({
      isOpen: true,
      event,
      mode: 'view'
    });
  };

  // Date click handler for creating new events
  const handleDateSelect = (date) => {
    setModalState({
      isOpen: true,
      event: date,
      mode: 'create'
    });
  };

  // Close modal
  const handleCloseModal = (nextMode) => {
    if (nextMode === 'edit' && modalState.event) {
      // Switch to edit mode
      setModalState({
        ...modalState,
        mode: 'edit'
      });
    } else {
      // Close the modal
      setModalState({
        isOpen: false,
        event: null,
        mode: 'view'
      });
    }
  };

  return (
    <div className="calendar-page">
      <div className="page-title">
        <h1>Calendar</h1>
        <button 
          className="add-event-button" 
          onClick={() => handleDateSelect(new Date())}
        >
          Add Event
        </button>
      </div>

      <CalendarProvider>
        <Calendar 
          onEventClick={handleEventClick}
          onDateSelect={handleDateSelect}
        />
        <EventModal 
          isOpen={modalState.isOpen}
          onClose={handleCloseModal}
          event={modalState.event}
          mode={modalState.mode}
        />
      </CalendarProvider>
    </div>
  );
};

// Example of how to fetch and set events from API
const CalendarPageWithData = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setIsLoading(true);
        const response = await bookingService.getAllBookings(1, 1000);
        
        // Transform bookings data into calendar events
        const calendarEvents = response.data.map(booking => ({
          id: booking.id,
          title: booking.title || `Booking #${booking.id}`,
          date: new Date(booking.startDate),
          durationInMinutes: calculateDuration(booking.startDate, booking.endDate),
          description: booking.description,
          location: booking.location,
          color: getStatusColor(booking.status),
          booking: booking // Keep the original booking data
        }));
        
        setEvents(calendarEvents);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setError('Failed to load bookings. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Calculate duration between start and end dates
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 60; // Default 1 hour
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end - start;
    
    return Math.max(30, Math.floor(durationMs / (1000 * 60))); // Convert to minutes with 30 min minimum
  };

  // Get color based on booking status
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return '#4CAF50'; // Green
      case 'pending':
        return '#FFC107'; // Yellow
      case 'cancelled':
        return '#F44336'; // Red
      default:
        return '#4285F4'; // Blue
    }
  };

  return (
    <CalendarProvider initialEvents={events}>
      <div className="calendar-page">
        {isLoading ? (
          <div className="loading-spinner">Loading...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <CalendarPage />
        )}
      </div>
    </CalendarProvider>
  );
};

export default CalendarPageWithData; 